import { createTheme } from '@mui/material'
import type { PaletteColor, PaletteColorOptions } from '@mui/material/styles'

declare module '@mui/material/styles' {
    interface TypographyVariants {
        body3: React.CSSProperties
        body4: React.CSSProperties
    }

    interface TypographyVariantsOptions {
        body3?: React.CSSProperties
        body4?: React.CSSProperties
        link?: React.CSSProperties
    }

    interface Palette {
        background1: PaletteColor
        background2: PaletteColor
        background3: PaletteColor
        background4: PaletteColor
        stroke1: PaletteColor
        stroke2: PaletteColor
        muted: PaletteColor
    }

    interface PaletteOptions {
        background1?: PaletteColorOptions
        background2?: PaletteColorOptions
        background3?: PaletteColorOptions
        background4?: PaletteColorOptions
        stroke1?: PaletteColorOptions
        stroke2?: PaletteColorOptions
        muted?: PaletteColorOptions
    }
}

declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        body3: true
        body4: true
        link: true
    }
}

// Helper function to create custom colors for easy access
export const customColors = {
    // Warning variants
    warning2: '#FFA001',
    warning3: '#FFBF3A',
    warningLight: '#FFF1DC',

    // Info variants
    info2: '#FFD058',

    // Additional colors that might be useful
    black: '#000000',
    white: '#FFFFFF'
} as const

export const defaultTheme = createTheme({
    palette: {
        // Primary Colors
        primary: {
            main: '#003B6F', // Primary main
            light: '#5A8FC3', // Can be used as lighter variant
            dark: '#002D52', // Darker variant for consistency
            contrastText: '#FFFFFF'
        },

        // Secondary Colors
        secondary: {
            main: '#5A8FC3', // Secondary main
            light: '#8BB2D9',
            dark: '#3A6FA3',
            contrastText: '#FFFFFF'
        },

        // Black & White
        common: {
            black: '#000000',
            white: '#FFFFFF'
        },

        // Background Colors
        background: {
            default: '#FFFFFF',
            paper: '#F9F9F9'
        },

        // Custom Background Colors
        background1: {
            main: '#E9F1FA', // Background1
            light: '#D2D9E1',
            dark: '#D4E7F5',
            contrastText: '#000000'
        },
        background2: {
            main: '#E2E8F0', // Background2
            light: '#F1F5F9',
            dark: '#CBD5E1',
            contrastText: '#000000'
        },
        background3: {
            main: '#F0F2F5', // Background3
            light: '#F8F9FA',
            dark: '#E9ECEF',
            contrastText: '#000000'
        },
        background4: {
            main: '#F4F8FB', // Background4
            light: '#FAFCFE',
            dark: '#E6F2F8',
            contrastText: '#000000'
        },

        // Warning Colors
        warning: {
            main: '#FF820E', // Warning main
            light: '#FFF1DC', // Warning light
            dark: '#E6740C',
            contrastText: '#FFFFFF'
        },

        // Error Colors
        error: {
            main: '#FE5D7C', // Error main
            light: '#FFE7EB', // Error light
            dark: '#E5536F',
            contrastText: '#FFFFFF'
        },

        // Success Colors
        success: {
            main: '#6FCF97', // Success main
            light: '#8EDB9A',
            dark: '#03C302', // Error Dark (as shown in image)
            contrastText: '#FFFFFF'
        },

        // Info Colors
        info: {
            main: '#079FF4', // Info1
            light: '#4FC3F7',
            dark: '#0680D1',
            contrastText: '#FFFFFF'
        },

        // Custom Stroke Colors
        stroke1: {
            main: '#CCDEEF', // Stroke main1
            light: '#E6F1F8',
            dark: '#B3CEE6',
            contrastText: '#000000'
        },
        stroke2: {
            main: '#99BCDF', // Stroke main2
            light: '#CCDDEE',
            dark: '#7AA8D6',
            contrastText: '#000000'
        },

        // Text Colors
        text: {
            primary: '#000000',
            secondary: '#ADBDCC',
            disabled: '#CCCCCC'
        },
        muted: {
            main: '#8A9BB0',
            light: '#62748E',
            dark: '#CBD5E1',
            contrastText: '#000000'
        }
    },

    typography: {
        fontFamily: 'var(--font-inter)',

        // Headings (H1-H4) - Poppins font
        h1: {
            fontFamily: 'var(--font-poppins)',
            fontSize: '20px',
            fontStyle: 'normal',
            fontWeight: 'var(--font-weight-bold)',
            lineHeight: 'var(--line-height-tight)'
        },
        h2: {
            fontFamily: 'var(--font-poppins)',
            fontSize: '18px',
            fontStyle: 'normal',
            fontWeight: 'var(--font-weight-bold)',
            lineHeight: 'var(--line-height-tight)'
        },
        h3: {
            fontFamily: 'var(--font-poppins)',
            fontSize: '18px',
            fontStyle: 'normal',
            fontWeight: 'var(--font-weight-normal)',
            lineHeight: 'var(--line-height-tight)'
        },
        h4: {
            fontFamily: 'var(--font-poppins)',
            fontSize: '22px',
            fontStyle: 'normal',
            fontWeight: 'var(--font-weight-bold)',
            lineHeight: 'var(--line-height-tight)'
        },

        // Body text (Body1-Body4) - Inter font
        body1: {
            fontFamily: 'var(--font-inter)',
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: 'var(--font-weight-regular)',
            lineHeight: '20px'
        },
        body2: {
            fontFamily: 'var(--font-inter)',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 'var(--font-weight-regular)',
            lineHeight: '20px'
        },
        body3: {
            fontFamily: 'var(--font-poppins)',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 'var(--font-weight-regular)',
            lineHeight: '20px'
        },
        body4: {
            fontFamily: 'var(--font-inter)',
            fontSize: '12px',
            fontStyle: 'normal',
            fontWeight: 'var(--font-weight-regular)',
            lineHeight: '20px'
        },

        // Subtitle - Inter font
        subtitle1: {
            fontFamily: 'var(--font-inter)',
            fontSize: '12px',
            fontStyle: 'normal',
            fontWeight: 'var(--font-weight-regular)',
            lineHeight: '14px',
            color: '#ADBDCC'
        },
        link: {
            fontFamily: 'var(--font-inter)',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 'var(--font-weight-regular)',
            lineHeight: '20px',
            color: 'primary.light'
        }
    },
    components: {
        MuiCard: {
            styleOverrides: {
                root: {
                    boxShadow: 'none',
                    borderRadius: '12px',
                    backgroundColor: '#FFFFFF',
                    padding: '0px'
                }
            }
        },
        MuiCardHeader: {
            styleOverrides: {
                root: {
                    padding: '16px 16px 0px 16px'
                }
            }
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    '&.small-card': {
                        padding: '16px'
                    },
                    '&.medium-card': {
                        padding: '24px'
                    }
                }
            }
        },
        MuiFormControlLabel: {
            styleOverrides: {
                label: {
                    fontFamily: 'var(--font-inter)',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 'var(--font-weight-regular)',
                    lineHeight: '20px',
                    color: 'rgba(90, 143, 195, 1)'
                }
            }
        },
        MuiButtonBase: {
            styleOverrides: {
                root: {
                    fontSize: '16px',
                    fontWeight: 'var(--font-weight-bold)',
                    fontFamily: 'var(--font-inter)',
                    '&.Mui-disabled': {
                        cursor: 'not-allowed',
                        backgroundColor: 'rgba(204, 222, 239, 1)!important',
                        color: '#ffffff!important'
                    }
                }
            }
        }
    }
})
